'use client';
import type { ProductConfCols } from '@eliadmin/core/src/api/proform/proform-types';
import type {
  ConfiguratorField,
  ConfiguratorFieldFnOptions,
} from '@eliadmin/core/src/components/configurator/configurator-field-section';
import { ConfigurationOrnament } from '@eliadmin/core/src/components/configurator/fields/configurator-ornament';
import { ConfigurationOrnamentResetHandler } from '@eliadmin/core/src/components/configurator/fields/configurator-ornament_reset';
import { ConfiguratorRadio } from '@eliadmin/core/src/components/configurator/fields/configurator-radio';
import { ConfigurationRange } from '@eliadmin/core/src/components/configurator/fields/configurator-range';
import { ConfiguratorSelect } from '@eliadmin/core/src/components/configurator/fields/configurator-select';
import { ConfigurationSwitch } from '@eliadmin/core/src/components/configurator/fields/configurator-switch';
import { ConfigurationTextarea } from '@eliadmin/core/src/components/configurator/fields/configurator-textarea';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import type { ProductConfIdColumn } from '@eliadmin/core/src/stores/configurator-store';
import {
  useConfiguratorStore,
  useConfiguratorValue,
} from '@eliadmin/core/src/stores/configurator-store';
import { extractDataFromField } from '@eliadmin/core/src/utils/proform-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import type { PropsWithChildren } from 'react';
import React from 'react';

export type ConfiguratorFieldProps = {
  field: ConfiguratorField;
};

function getComponentFromType<T extends ConfiguratorField['type']>(
  type: ConfiguratorField['type'],
) {
  const map: {
    [K in ConfiguratorField['type']]: React.FC<FieldProps<K>>;
  } = {
    select: ConfiguratorSelect,
    radio: ConfiguratorRadio,
    range: ConfigurationRange,
    switch: ConfigurationSwitch,
    textarea: ConfigurationTextarea,
    ornament: ConfigurationOrnament,
    ornament_reset: ConfigurationOrnamentResetHandler,
  };

  return map[type] as unknown as React.FC<FieldProps<T>>;
}

export type FieldProps<T extends ConfiguratorField['type']> = {
  fieldConfig: ProductConfCols['root']['smako_conf_cols']['column'][number];
  field: Extract<ConfiguratorField, { type: T }>;
};

export function useConfiguratorField<T extends ConfiguratorField['type']>(
  fieldCol: FieldProps<T>['fieldConfig'],
) {
  const conf = useConfiguratorStore(s => s.productConf);
  const fieldData = extractDataFromField(fieldCol.column as ProductConfIdColumn, conf) ?? null;
  // const setFieldData = useConfiguratorStore(s => s.setSelectFieldData);

  const configuratorValue = useConfiguratorValue(fieldCol.column);

  // const onChangeOverride = useCallback(
  //   (value: string) => {
  //     configuratorValue.onChange(value);

  //     if (fieldData) {
  //       const selectedFieldData = fieldData.find(item => item.kod === value);
  //       setFieldData(fieldCol.column as any, selectedFieldData ?? null);
  //     }
  //   },
  //   [configuratorValue, fieldCol.column, fieldData, setFieldData],
  // );

  return {
    fieldData,
    conf,
    ...configuratorValue,
    // onChange: onChangeOverride,
  };
}

export function useConfiguratorFieldOptions(
  fieldProps: FieldProps<ConfiguratorField['type']>,
): ConfiguratorFieldFnOptions {
  const productConf = useConfiguratorStore(s => s.productConf);
  const { productConfId, value } = useConfiguratorValue(fieldProps.fieldConfig.column);
  return {
    fieldProps,
    productConfId,
    productConf,
    value,
  };
}

function ConfiguratorFieldWrapper(props: PropsWithChildren<FieldProps<ConfiguratorField['type']>>) {
  const options = useConfiguratorFieldOptions(props);

  const isVisible = props.field.isVisible?.(options) ?? true;
  if (!isVisible) return null;
  return props.children;
}

export function ConfiguratorField(props: ConfiguratorFieldProps) {
  const cols = useConfiguratorStore(s => s.productCols);
  const confId = useConfiguratorStore(s => s.user_konf_id);
  const { t } = useTranslations();
  // const visibilitySettings = useConfiguratorStore(s => s.visibilitySettings);

  const Component = getComponentFromType(props.field.type);
  if (!Component) throw new Error(`Unknown field type: ${props.field.type}`);

  let fieldCol = cols.root.smako_conf_cols?.column.find(col => col?.column === props.field?.id);

  if (props.field.type === 'range' && props.field.variant === 'mnozstvi') {
    fieldCol = {
      column: 'mnozstvi',
      help: t('text1464'),
      label: t('text1465'),
      must: '1',
    };
  }
  if (props.field.type === 'ornament') {
    const index = Number(props.field.id.replace('user_m', ''));
    fieldCol = {
      column: props.field.id,
      help: '',
      label: `${index}.`,
      must: '0',
    };
  }
  if (!fieldCol) return null;
  // if (visibilitySet∏tings[fieldCol.column] === false) return null;

  return (
    <ConfiguratorFieldWrapper fieldConfig={fieldCol} field={props.field}>
      <div className={cn('col-span-1', props.field.className)}>
        <Component fieldConfig={fieldCol} field={props.field} key={confId} />
      </div>
    </ConfiguratorFieldWrapper>
  );
}
